/**
 * Explore's Share menu, AgencyAnalytics' four actions:
 * - Download PDF: the view becomes a client report (explore-report.ts) and
 *   its PDF is made in the background, then downloaded here when ready.
 * - Download XLS: immediate, the view's main table only — one sheet named
 *   after the source, a header row of the dimension and enabled metrics, raw
 *   numbers (durations in seconds, money in currency units, rates as
 *   fractions), saved as <Client>_<Source>_<start>_<end>.xlsx.
 * - Share Link: a link to that report that viewers can re-date.
 * - Add To Report: the report is created and listed with the client's reports.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { exportsApi } from '../../reports/exports-api.ts';
import type { CatalogueMetric, WidgetResult } from '../../reports/reports-api.ts';
import { sharesApi } from '../../reports/shares-api.ts';
import { downloadXlsx, type Cell } from '../../reports/xlsx.ts';
import { go } from '../routes.ts';
import { createExploreReport, type ExploreSnapshot } from './explore-report.ts';

const CENTS = 100;
const POLL_MS = 3000;
const MAX_POLLS = 60;

type Status = { tone: 'ok' | 'error' | 'busy'; text: string; url?: string } | null;

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function waitForPdf(
  organisationId: string,
  documentId: string,
  exportId: string,
): Promise<Blob> {
  for (let poll = 0; poll < MAX_POLLS; poll += 1) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    const item = (await exportsApi.list(organisationId, documentId)).find(
      (entry) => entry.id === exportId,
    );
    if (item?.status === 'DONE') return exportsApi.file(organisationId, exportId);
    if (item?.status === 'FAILED') throw new Error(item.error ?? 'The PDF could not be made.');
  }
  throw new Error(
    'The PDF is taking longer than usual. It will be in the report’s Downloads when ready.',
  );
}

export function ExploreShare(props: {
  organisationId: string;
  onReportCreated: () => void;
  snapshot: ExploreSnapshot;
  fileParts: string[];
  dimensionLabel: string;
  metrics: CatalogueMetric[];
  result: WidgetResult | null;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const onPointer = (event: PointerEvent) =>
      box.current && !box.current.contains(event.target as Node) && setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const rows = props.result?.rows ?? [];
  const baseName = props.fileParts
    .map((part) => part.replace(/[\\/:*?"<>|_]/g, ' ').trim())
    .join('_');
  const run = (busy: string, work: () => Promise<Status>) => {
    setOpen(false);
    setStatus({ tone: 'busy', text: busy });
    work().then(setStatus, (problem: unknown) =>
      setStatus({
        tone: 'error',
        text: problem instanceof Error ? problem.message : 'That did not work.',
      }),
    );
  };
  const report = async () => {
    const documentId = await createExploreReport(props.organisationId, props.snapshot);
    props.onReportCreated();
    return documentId;
  };

  const downloadXls = () => {
    const header: Cell[] = [props.dimensionLabel, ...props.metrics.map((metric) => metric.label)];
    const body: Cell[][] = rows.map((row) => [
      row.label,
      ...props.metrics.map((metric) => {
        const value = row.values[metric.key] ?? null;
        return value !== null && metric.format === 'money' ? value / CENTS : value;
      }),
    ]);
    downloadXlsx(baseName, [{ name: 'Google Analytics 4', rows: [header, ...body] }]);
    setOpen(false);
  };
  const downloadPdf = () =>
    run('Making the PDF…', async () => {
      const documentId = await report();
      const item = await exportsApi.request(props.organisationId, documentId, props.snapshot.range);
      saveBlob(await waitForPdf(props.organisationId, documentId, item.id), `${baseName}.pdf`);
      return { tone: 'ok', text: 'PDF downloaded. The report is also in the client’s Reports.' };
    });
  const shareLink = () =>
    run('Creating the link…', async () => {
      const documentId = await report();
      const link = await sharesApi.create(props.organisationId, documentId, {
        name: `${props.snapshot.client.name} Google Analytics 4`,
        allow_range_change: true,
        range: props.snapshot.range,
      });
      return { tone: 'ok', text: 'Share link ready:', url: link.url };
    });
  const addToReport = () =>
    run('Adding to a report…', async () => {
      await report();
      go({ area: 'client', clientId: props.snapshot.client.id, tab: 'reports', documentId: null });
      return null;
    });

  return (
    <div className="ex-filter" ref={box}>
      <button
        type="button"
        className="btn secondary"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        Share
      </button>
      {open && (
        <ul className="ex-pop ex-pop-list" role="menu">
          <li role="none">
            <button type="button" role="menuitem" onClick={downloadPdf}>
              Download PDF
            </button>
          </li>
          <li role="none">
            <button
              type="button"
              role="menuitem"
              disabled={rows.length === 0}
              onClick={downloadXls}
            >
              Download XLS
            </button>
          </li>
          <li role="none">
            <button type="button" role="menuitem" onClick={shareLink}>
              Share Link
            </button>
          </li>
          <li role="none">
            <button type="button" role="menuitem" onClick={addToReport}>
              Add To Report
            </button>
          </li>
        </ul>
      )}
      {status && (
        <p
          className={`ex-share-status ${status.tone}`}
          role={status.tone === 'error' ? 'alert' : 'status'}
        >
          {status.text}{' '}
          {status.url && (
            <>
              <a href={status.url} target="_blank" rel="noreferrer">
                {status.url}
              </a>{' '}
              <button
                type="button"
                className="ex-link"
                onClick={() => void navigator.clipboard?.writeText(status.url ?? '')}
              >
                Copy
              </button>
            </>
          )}
          {status.tone !== 'busy' && (
            <button
              type="button"
              className="ex-link"
              aria-label="Dismiss"
              onClick={() => setStatus(null)}
            >
              ×
            </button>
          )}
        </p>
      )}
    </div>
  );
}
